// ConfiguracionML — /admin/canales/mercadolibre/configuracion
// Pantalla técnica: configurar App ID + Client Secret, iniciar OAuth, ver tokens vivos,
// ver inbox de webhooks, gestionar test users.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const ML_AUTH_BASE = 'https://auth.mercadolibre.com.uy/authorization'
const REDIRECT_URI = 'https://panel.limpack.uy/admin/canales/ml/callback'

export default function ConfiguracionML() {
  const [loading, setLoading] = useState(true)
  const [cred, setCred] = useState(null)
  const [webhookLog, setWebhookLog] = useState([])
  const [testUsers, setTestUsers] = useState([])
  const [form, setForm] = useState({ app_id: '', client_secret: '' })
  const [msg, setMsg] = useState(null)
  const [showEditCred, setShowEditCred] = useState(false)

  async function cargar() {
    setLoading(true)
    const [credRes, inboxRes, testRes] = await Promise.all([
      supabase.from('ml_credentials').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('ml_webhook_inbox').select('*').order('received_at', { ascending: false }).limit(10),
      supabase.from('ml_test_users').select('*').order('created_at', { ascending: false }),
    ])
    setCred(credRes.data)
    setWebhookLog(inboxRes.data || [])
    setTestUsers(testRes.data || [])
    if (!credRes.data) setShowEditCred(true)
    setLoading(false)
  }
  useEffect(() => { cargar() }, [])

  async function guardarCred() {
    if (!form.app_id || !form.client_secret) {
      setMsg({ tipo: 'err', txt: 'App ID y Client Secret requeridos' })
      return
    }
    const payload = {
      app_id: form.app_id.trim(),
      client_secret_encrypted: form.client_secret.trim(),
      redirect_uri: REDIRECT_URI,
      site_id: 'MLU',
    }
    let resp
    if (cred?.id) {
      resp = await supabase.from('ml_credentials').update(payload).eq('id', cred.id)
    } else {
      resp = await supabase.from('ml_credentials').insert(payload)
    }
    if (resp.error) {
      setMsg({ tipo: 'err', txt: resp.error.message })
      return
    }
    setMsg({ tipo: 'ok', txt: 'Credenciales guardadas. Ahora podés conectar.' })
    setShowEditCred(false)
    setForm({ app_id: '', client_secret: '' })
    cargar()
  }

  function iniciarOAuth() {
    if (!cred?.app_id) {
      setMsg({ tipo: 'err', txt: 'Falta App ID. Cargá las credenciales primero.' })
      return
    }
    const state = crypto.randomUUID().slice(0, 16)
    sessionStorage.setItem('ml_oauth_state', state)
    const url = `${ML_AUTH_BASE}?response_type=code&client_id=${encodeURIComponent(cred.app_id)}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&state=${state}`
    window.location.href = url
  }

  async function desconectar() {
    if (!confirm('¿Desconectar de MercadoLibre? Vas a perder los tokens. Las credenciales (App ID + Secret) quedan.')) return
    const { error } = await supabase.from('ml_credentials').update({
      access_token: null,
      access_token_expires_at: null,
      refresh_token: null,
      refresh_token_expires_at: null,
      user_id_ml: null,
      nickname: null,
    }).eq('id', cred.id)
    if (error) { setMsg({ tipo: 'err', txt: error.message }); return }
    setMsg({ tipo: 'ok', txt: 'Desconectado.' })
    cargar()
  }

  const conectado = !!cred?.access_token
  const fmt = (s) => s ? new Date(s).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' }) : '—'
  const fmtRel = (s) => {
    if (!s) return '—'
    const diff = (new Date(s).getTime() - Date.now()) / 1000
    const absMin = Math.abs(diff) / 60
    if (absMin < 60) return `${Math.round(absMin)} min`
    if (absMin < 1440) return `${Math.round(absMin / 60)}h`
    return `${Math.round(absMin / 1440)}d`
  }

  return (
    <AdminLayout title="ML Configuración">
      <div className="cm-wrap">
        <header className="cm-head">
          <div>
            <div className="cm-eyebrow">
              <Link to="/admin/canales/mercadolibre">← MercadoLibre</Link> · Configuración
            </div>
            <h1 className="cm-title">Conexión con MercadoLibre</h1>
            <div className="cm-sub">OAuth, refresh token, suscripciones a webhooks y test users para desarrollo.</div>
          </div>
        </header>

        {msg && (
          <div className={`cm-msg ${msg.tipo}`} onClick={() => setMsg(null)}>
            {msg.txt}<span style={{ float: 'right', cursor: 'pointer' }}>×</span>
          </div>
        )}

        {/* Banner: app de prueba pre-SAS */}
        {cred?.app_id === '3453365654111094' && (
          <div style={{
            marginTop: 14, padding: '14px 18px',
            background: '#FEF3C7', border: '2px solid #FCD34D', borderRadius: 12,
            display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 28 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 240 }}>
              <strong style={{ color: '#78350F', fontSize: 14 }}>App de prueba pre-SAS — bajo cuenta personal de Maria</strong>
              <div style={{ fontSize: 12, color: '#92400E', marginTop: 4, lineHeight: 1.5 }}>
                Esta app (ID <code>3453365654111094</code>) está creada en la cuenta personal de Maria, no en la cuenta SAS de Limpack. Sirve para validar el flow end-to-end pero <strong>las publicaciones aparecen a nombre de Maria</strong> y los CFE saldrían a su nombre. <strong>BLOQUEANTE pre-lanzamiento:</strong> cuando se habilite la SAS Limpack en MP/ML, hay que crear app nueva desde la cuenta SAS, reconectar OAuth y republicar productos. Detalle en <code>PENDIENTES.md</code> → "BLOQUEANTE pre-lanzamiento ML".
              </div>
            </div>
          </div>
        )}

        {/* Estado conexión */}
        <div className="cm-status-card">
          <div className={`cm-status-icon ${conectado ? 'ok' : 'warn'}`}>{conectado ? '✓' : '⚠'}</div>
          <div style={{ flex: 1 }}>
            <div className="cm-status-name">{conectado ? `Conectado a MercadoLibre Uruguay` : 'Sin conectar'}</div>
            <div className="cm-status-meta">
              {conectado
                ? <>Vendedor: <strong>@{cred.nickname}</strong> · User ID: <code>{cred.user_id_ml}</code></>
                : cred?.app_id
                  ? <>App ID cargado. Falta autorizar al vendedor.</>
                  : <>Cargá el App ID y Client Secret de la app developer.</>
              }
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {conectado ? (
              <button className="cm-btn-ghost" onClick={desconectar}>Desconectar</button>
            ) : cred?.app_id ? (
              <button className="cm-btn-primary" onClick={iniciarOAuth}>Conectar con ML →</button>
            ) : null}
            <button className="cm-btn-ghost" onClick={() => setShowEditCred(s => !s)}>
              {cred?.app_id ? 'Editar credenciales' : 'Cargar credenciales'}
            </button>
          </div>
        </div>

        {/* Form credenciales */}
        {showEditCred && (
          <div className="cm-card">
            <h3>App developer (App ID + Client Secret)</h3>
            <div style={{ fontSize: 12, color: '#5F5E5A', marginBottom: 14, lineHeight: 1.5 }}>
              Las creás en <a href="https://developers.mercadolibre.com.uy/devcenter" target="_blank" rel="noreferrer" style={{ color: '#00A8B5' }}>developers.mercadolibre.com.uy/devcenter</a> → tu app. Redirect URI debe ser exactamente: <code>{REDIRECT_URI}</code>
            </div>
            <div className="cm-form-grid">
              <div>
                <label className="cm-lbl">App ID (Client ID)</label>
                <input
                  type="text"
                  value={form.app_id}
                  onChange={e => setForm(f => ({ ...f, app_id: e.target.value }))}
                  placeholder="ej. 8421984675321099"
                  className="cm-input"
                />
              </div>
              <div>
                <label className="cm-lbl">Client Secret</label>
                <input
                  type="password"
                  value={form.client_secret}
                  onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))}
                  placeholder={cred?.app_id ? 'dejar vacío para no cambiar' : 'string largo de la app'}
                  className="cm-input"
                />
                <div className="cm-help">No se muestra después de guardar. Solo el service_role lo lee para refrescar tokens.</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowEditCred(false); setForm({ app_id: '', client_secret: '' }) }} className="cm-btn-ghost">Cancelar</button>
              <button onClick={guardarCred} className="cm-btn-primary">Guardar</button>
            </div>
          </div>
        )}

        {conectado && (
          <div className="cm-two-col">
            <div className="cm-card">
              <h3>Tokens vivos</h3>
              <table className="cm-simple">
                <tbody>
                  <tr><td>Access token</td><td style={{ textAlign: 'right' }}><span style={{ color: '#1D9E75', fontWeight: 600 }}>activo</span> · vence en {fmtRel(cred.access_token_expires_at)}</td></tr>
                  <tr><td>Refresh token</td><td style={{ textAlign: 'right' }}><span style={{ color: '#1D9E75', fontWeight: 600 }}>activo</span> · vence en {fmtRel(cred.refresh_token_expires_at)}</td></tr>
                  <tr><td>Última renovación</td><td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 11 }}>{fmt(cred.last_refresh_at)}</td></tr>
                  <tr><td>Lock activo</td><td style={{ textAlign: 'right' }}>{cred.is_locked ? <span style={{ color: '#C53030' }}>SÍ · {fmt(cred.locked_at)}</span> : <span style={{ color: '#5F5E5A' }}>no</span>}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="cm-card">
              <h3>Tópicos suscritos</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(cred.topics_subscribed || []).map(t => (
                  <span key={t} className="cm-chip">{t}</span>
                ))}
              </div>
              <div className="cm-help" style={{ marginTop: 10 }}>
                Los tópicos los configurás en el panel developer de ML. Acá solo los listamos.
              </div>
            </div>
          </div>
        )}

        {/* Inbox webhooks */}
        <div className="cm-card">
          <h3>Webhook inbox · últimas 10 notificaciones</h3>
          <div className="cm-help" style={{ marginBottom: 10 }}>
            ML manda notificaciones que tenemos que responder en &lt; 500ms con 200. Patrón: insert en inbox (dedup por _id) → return 200 → procesar async via pg_cron.
          </div>
          {webhookLog.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: '#888780' }}>
              Sin notificaciones todavía. Cuando ML mande la primera, aparece acá.
            </div>
          ) : (
            <table className="cm-simple">
              <thead><tr><th>Recibido</th><th>Tópico</th><th>Recurso</th><th>Estado</th></tr></thead>
              <tbody>
                {webhookLog.map(w => (
                  <tr key={w.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{fmt(w.received_at)}</td>
                    <td><span className={`cm-topic ${w.topic.split('_')[0]}`}>{w.topic}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{w.resource}</td>
                    <td>{w.processed_at ? <span style={{ color: '#1D9E75' }}>✓ procesado</span> : <span style={{ color: '#BA7517' }}>pendiente</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Test users */}
        <div className="cm-card">
          <h3>Test users <span style={{ fontSize: 11, color: '#888780', fontWeight: 400 }}>· {testUsers.length} de 10 creados</span></h3>
          <div className="cm-help" style={{ marginBottom: 10 }}>
            ML permite hasta 10 test users por cuenta developer. Permiten probar publicaciones y ventas sin tocar la cuenta real ni gastar comisiones. Útil mientras <strong>LIMPACKUY</strong> esté bloqueada por <em>day-1</em>.
          </div>
          {testUsers.length === 0 ? (
            <div style={{ padding: 14, fontSize: 12, color: '#888780', background: '#F8F6F1', borderRadius: 6 }}>
              Sin test users. Una vez que tengas el OAuth funcionando, los creás desde acá llamando a <code>POST /users/test_user</code>.
            </div>
          ) : (
            <table className="cm-simple">
              <thead><tr><th>Nickname</th><th>Email</th><th>Password</th><th>Creado</th></tr></thead>
              <tbody>
                {testUsers.map(t => (
                  <tr key={t.id}>
                    <td><strong>{t.nickname}</strong></td>
                    <td style={{ fontSize: 11 }}>{t.email}</td>
                    <td><code>{t.password}</code></td>
                    <td>{fmt(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

function Estilos() {
  return (
    <style>{`
      .cm-wrap { max-width: 1300px; margin: 0 auto; padding: 0 4px 60px; }
      .cm-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; }
      .cm-eyebrow { font-size: 11px; letter-spacing: 0.5px; color: #888780; }
      .cm-eyebrow a { color: #00A8B5; text-decoration: none; }
      .cm-title { margin: 4px 0; font-size: 22px; font-weight: 600; color: #1A3461; }
      .cm-sub { font-size: 12.5px; color: #5F5E5A; }

      .cm-msg { padding: 9px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 12.5px; cursor: pointer; }
      .cm-msg.ok { background: #E1F5EE; border: 1px solid #1D9E75; color: #085041; }
      .cm-msg.err { background: #FDEEEE; border: 1px solid #C53030; color: #791F1F; }

      .cm-status-card { display: flex; gap: 14px; padding: 14px 18px; border: 1px solid #E6E2D8; border-radius: 10px; background: #fff; margin-bottom: 12px; align-items: center; }
      .cm-status-icon { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; }
      .cm-status-icon.ok { background: #E1F5EE; color: #085041; }
      .cm-status-icon.warn { background: #FAEEDA; color: #633806; }
      .cm-status-name { font-size: 14px; font-weight: 600; color: #1A3461; }
      .cm-status-meta { font-size: 11.5px; color: #5F5E5A; margin-top: 3px; }
      .cm-status-meta code { font-family: 'SF Mono', monospace; background: #F8F6F1; padding: 1px 5px; border-radius: 3px; }

      .cm-card { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 16px; margin-bottom: 12px; }
      .cm-card h3 { margin: 0 0 10px; font-size: 13px; font-weight: 600; color: #1A3461; }
      .cm-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
      .cm-two-col .cm-card { margin: 0; }

      .cm-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      .cm-lbl { display: block; font-size: 10.5px; letter-spacing: 0.5px; color: #888780; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
      .cm-input { width: 100%; padding: 8px 10px; border: 1px solid #E6E2D8; border-radius: 6px; font-size: 13px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box; }
      .cm-help { font-size: 11px; color: #888780; margin-top: 4px; line-height: 1.45; }
      .cm-help code { font-family: 'SF Mono', monospace; background: #F8F6F1; padding: 1px 5px; border-radius: 3px; color: #00A8B5; font-size: 11px; }

      .cm-btn-primary { background: #00A8B5; color: #fff; border: 0; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
      .cm-btn-ghost { background: #fff; color: #1A3461; border: 1px solid #E6E2D8; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 500; cursor: pointer; font-family: inherit; }

      .cm-simple { width: 100%; border-collapse: collapse; font-size: 12.5px; }
      .cm-simple th { text-align: left; padding: 8px 10px; background: #F8F6F1; font-size: 10.5px; letter-spacing: 0.5px; text-transform: uppercase; color: #888780; font-weight: 600; border-bottom: 1px solid #E6E2D8; }
      .cm-simple td { padding: 9px 10px; border-bottom: 1px solid #F1EFE8; color: #1A3461; }
      .cm-simple code { font-family: 'SF Mono', monospace; background: #F8F6F1; padding: 1px 5px; border-radius: 3px; font-size: 11px; }

      .cm-chip { padding: 3px 9px; border-radius: 999px; font-size: 10.5px; background: #EEEDFE; color: #534AB7; font-weight: 600; }
      .cm-topic { padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 600; }
      .cm-topic.orders { background: #E1F5EE; color: #085041; }
      .cm-topic.items { background: #E0F4F6; color: #00A8B5; }
      .cm-topic.questions { background: #FAEEDA; color: #633806; }
      .cm-topic.shipments { background: #EEEDFE; color: #534AB7; }
      .cm-topic.messages { background: #F1EFE8; color: #5F5E5A; }
    `}</style>
  )
}
